<script lang="ts">
  /**
   * THE dashboard's thread panel: a post, its comments, and a reply box.
   *
   * TWO DRAWERS BECAME THIS. The moderation inbox opened one that led with a composer
   * and the timeline opened another that was "deliberately NOT" it — but the difference
   * was never structural. Both loaded a thread into a right-hand panel, both scrolled it
   * as one column, and one of them had quietly drifted: the timeline's copy hand-rolled
   * the scrim, the Escape handler and the focus move that `Drawer` already owns, and
   * offered its reply through a per-card composer that had been unsendable for as long
   * as `CommentThread` had been forgetting to forward its save callback.
   *
   * WHAT SURVIVED FROM EACH is the better half of both: the timeline's chrome — the
   * primary spine, the slide, the engagement readout — because it is the more finished
   * of the two, and the inbox's actions, because reading a thread and answering it are
   * the same visit. The body is `@ui`'s `<PostThread>`, shared with the Helper panel.
   *
   * THE COMPOSER IS OPT-IN, on `onSubmit`. A surface with nothing to send to — a
   * read-only audit view — passes nothing and gets a reader, with no dead Reply
   * affordances on any row.
   */
  import { fly, fade } from 'svelte/transition';
  import { cubicOut } from 'svelte/easing';
  import PostThread, { type ThreadTarget } from '@ui/PostThread.svelte';
  import ReplyComposer from '@ui/composer/ReplyComposer.svelte';
  import StateBlock from '@ui/StateBlock.svelte';
  import { navigate } from '../router.svelte';
  import { handle401, type PostDetail } from '../admin';

  let {
    open,
    onclose,
    skoolId,
    target,
    detail = undefined,
    loading = false,
    error = null,
    onretry = null,
    eyebrow = null,
    postTitle = null,
    skoolUrl = null,
    onSubmit = null,
    onMarkAnswered = null,
    now = Date.now(),
  }: {
    open: boolean;
    onclose: () => void;
    skoolId: number | null;
    /** The row the panel opens on — and the row the reply box starts under. */
    target: ThreadTarget | null;
    /**
     * The thread, already loaded, for a caller that fetched it itself. The timeline
     * pages a cache of opened posts through this panel; omit it and the panel loads
     * the target for itself.
     */
    detail?: PostDetail | null | undefined;
    /** The caller's load state — only meaningful alongside `detail`. */
    loading?: boolean;
    error?: string | null;
    onretry?: (() => void) | null;
    /** The panel's kicker: a moderation reason, a post category, a section name. */
    eyebrow?: string | null;
    /** The post a targeted COMMENT sits under, so the header says what "on:" means. */
    postTitle?: string | null;
    skoolUrl?: string | null;
    /**
     * Queue the reply for a Helper to send. Absent means this panel does not answer.
     * The target is the composer's own — a thread is opened on one row and the reader
     * may move the box to any other.
     */
    /** Returning a word overrides the composer's confirmation — "Sent" where a
     *  Helper took it there and then, rather than the default "Queued". Passed
     *  straight through: narrowing this to `void` silently discarded it. */
    onSubmit?: ((to: ThreadTarget, text: string) => Promise<string | void>) | null;
    onMarkAnswered?: (() => Promise<void> | void) | null;
    now?: number;
  } = $props();

  let markBusy = $state(false);

  const post = $derived(detail?.post ?? null);
  const commentCount = $derived(detail?.comments.length ?? null);

  /** Honour reduced-motion: skip the slide, just show and hide. */
  const reduce =
    typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const slideIn = { x: 460, duration: reduce ? 0 : 280, opacity: 1, easing: cubicOut };
  const scrimIn = { duration: reduce ? 0 : 180 };

  let panel = $state<HTMLElement | null>(null);

  /**
   * Scroll the targeted comment into view once the thread is in.
   *
   * A thread opened FROM a list is opened to read one row of it, and on a
   * thirty-comment thread that row is far below the fold — the ring marks it, and the
   * reader never sees the ring. `<CommentThread>` puts `data-targeted` on it; this waits
   * for the paint that follows the fetch, then centres it.
   *
   * Keyed on the target and the thread, so re-opening the panel on a different comment
   * of the SAME post scrolls again rather than sitting where the last read left it.
   */
  function focusTarget(): void {
    if (target?.kind !== 'comment') return;
    // Two frames: the first is the paint that adds the comments, the second is when
    // their heights are settled enough for `scrollIntoView` to land on the right row.
    requestAnimationFrame(() =>
      requestAnimationFrame(() =>
        panel?.querySelector('[data-targeted]')?.scrollIntoView({
          block: 'center',
          behavior: 'smooth',
        }),
      ),
    );
  }

  /** A caller-owned thread is already here when the panel opens, so there is no load
   *  event to wait for — `onLoaded` covers the case where this fetches its own. */
  $effect(() => {
    if (open && detail && target?.kind === 'comment') focusTarget();
  });
  $effect(() => {
    if (!open) return;
    function onkey(e: KeyboardEvent): void {
      if (e.key === 'Escape') onclose();
    }
    window.addEventListener('keydown', onkey);
    panel?.focus();
    return () => window.removeEventListener('keydown', onkey);
  });

  /** This app navigates away on a 401 rather than rendering anything, which is the
   *  one thing it does differently from the panel on a failed thread read. */
  function explain(err: unknown): string | null {
    if (handle401(err, navigate)) return null;
    return err instanceof Error ? err.message : 'Failed to load the thread.';
  }

  async function mark(): Promise<void> {
    if (!onMarkAnswered || markBusy) return;
    markBusy = true;
    try {
      await onMarkAnswered();
    } finally {
      markBusy = false;
    }
  }
</script>

{#snippet composer(_detail: PostDetail, replyingTo: ThreadTarget)}
  <!-- Keyed on the ACTIVE row, not the opened one: moving the box to another comment
       must start it empty, not carry half a sentence aimed at someone else. -->
  {#key `${replyingTo.kind}:${replyingTo.id}`}
    <ReplyComposer
      {skoolId}
      kind={replyingTo.kind}
      targetId={replyingTo.id}
      onSubmit={onSubmit ? (text) => onSubmit(replyingTo, text) : null}
      submitLabel="Reply"
      doneLabel="Queued"
    />
  {/key}
{/snippet}

{#if open}
  <!-- Scrim: a light, un-blurred dim so the solid panel — not a frosted overlay —
       carries the focus. Click-to-close is a mouse affordance; Escape (window
       listener above) covers the keyboard. -->
  <!-- svelte-ignore a11y_click_events_have_key_events -->
  <div
    class="fixed inset-0 z-40 flex justify-end bg-base-content/25"
    transition:fade={scrimIn}
    onclick={onclose}
    role="presentation"
  >
    <div
      bind:this={panel}
      class="relative flex h-full w-full flex-col overflow-hidden bg-base-100 shadow-2xl outline-none sm:max-w-2xl"
      transition:fly={slideIn}
      onclick={(e) => e.stopPropagation()}
      role="dialog"
      aria-modal="true"
      aria-label="Post and discussion"
      tabindex="-1"
    >
      <!-- Signature: a slim primary spine anchoring the panel's leading edge, the
           same accent that marks the selected card in the feed. -->
      <div class="pointer-events-none absolute inset-y-0 left-0 w-[3px] bg-gradient-to-b from-primary via-primary/70 to-primary/20"></div>

      <header class="flex items-center gap-3 border-b hair px-5 py-3 pl-6">
        <div class="min-w-0 flex-1">
          <div class="eyebrow text-primary/80">{eyebrow ?? 'Discussion'}</div>
          {#if post}
            <div class="mt-1 flex items-center gap-3 font-mono text-[0.72rem] text-base-content/68">
              <span class="inline-flex items-center gap-1" title="Upvotes">
                <svg class="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M7 10v11" /><path d="M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2a3.13 3.13 0 0 1 3 3.88Z" /></svg>
                {post.upvotes ?? 0}
              </span>
              <span class="inline-flex items-center gap-1" title="Comments">
                <svg class="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>
                {commentCount ?? post.comment_count ?? 0}
              </span>
            </div>
          {:else if postTitle}
            <!-- A post title is the customer's own content: marked here, at the leaf, so
                 every caller inherits it rather than each one remembering. -->
            <div class="redact mt-0.5 truncate text-xs text-base-content/68">on: {postTitle}</div>
          {/if}
        </div>
        {#if skoolUrl ?? post?.url}
          <a
            href={skoolUrl ?? post?.url}
            target="_blank"
            rel="noopener noreferrer"
            class="inline-flex items-center gap-1 text-sm font-medium text-primary/80 transition-colors hover:text-primary"
            title="Open on Skool"
          >
            Skool
            <svg class="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M7 17 17 7" /><polyline points="7 7 17 7 17 17" /></svg>
          </a>
        {/if}
        {#if onMarkAnswered}
          <button class="btn btn-sm btn-outline btn-success" onclick={mark} disabled={markBusy}>
            {markBusy ? 'Marking…' : 'Mark answered'}
          </button>
        {/if}
        <button
          class="flex h-8 w-8 shrink-0 items-center justify-center rounded-field text-base-content/68 transition-colors hover:bg-base-200 hover:text-base-content"
          onclick={onclose}
          aria-label="Close"
        >
          <svg class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
        </button>
      </header>

      <!-- Body: the post, then its thread — the whole panel scrolls as one column. -->
      <div class="min-h-0 flex-1 overflow-y-auto px-5 py-5 pl-6">
        {#if loading && !detail}
          <StateBlock state="loading" compact />
        {:else if error && !detail}
          <StateBlock state="error" compact message={error} {onretry} />
        {:else}
          <PostThread
            {skoolId}
            {target}
            {detail}
            errorText={explain}
            onLoaded={focusTarget}
            {now}
            composer={onSubmit ? composer : undefined}
          />
        {/if}
      </div>
    </div>
  </div>
{/if}
